import React, { useState, useEffect, useCallback } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const SECTIONS = [
  { key: "overview",          label: "Overview" },
  { key: "platforms",         label: "Platforms" },
  { key: "social_media",      label: "Social Media" },
  { key: "audience",          label: "Audience" },
  { key: "demographics",      label: "Demographics" },
  { key: "technology_devices",label: "Technology & Devices" },
  { key: "trending",          label: "Trending" },
];

const ROLE_LABELS = { super_admin: "Super Admin", admin: "Admin", editor: "Editor", viewer: "Viewer" };
const ROLE_COLORS = {
  super_admin: "#a78bfa", admin: "#60a5fa", editor: "#34d399", viewer: "#94a3b8",
};

export function PermissionsMatrix({ currentUser, isSuperAdmin, theme: t, isMobile }) {
  const [users,   setUsers]   = useState([]);
  const [draft,   setDraft]   = useState({});   // { userId: { sectionKey: bool } }
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [msg,     setMsg]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API}/api/admin/users`);
      if (!res.ok) throw new Error((await res.json()).error);
      const data = await res.json();
      setUsers(data);
      // Seed draft from current permissions
      const d = {};
      for (const u of data) {
        d[u.id] = u.permissions
          ? { ...u.permissions }
          : Object.fromEntries(SECTIONS.map(s => [s.key, true])); // legacy all-access
      }
      setDraft(d);
    } catch (e) { setMsg(`✗ ${e.message}`); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  function toggle(userId, sectionKey) {
    // super_admin and admin always have full access — don't allow editing their perms
    const u = users.find(x => x.id === userId);
    if (!u || u.role === "super_admin" || u.role === "admin") return;
    setDraft(d => ({ ...d, [userId]: { ...d[userId], [sectionKey]: !d[userId]?.[sectionKey] } }));
  }

  async function saveAll() {
    setSaving(true); setMsg("");
    const updates = users
      .filter(u => u.role !== "super_admin" && u.role !== "admin")
      .map(u => ({ id: u.id, permissions: draft[u.id] || {} }));

    try {
      const res = await fetchWithAuth(`${API}/api/admin/permissions`, {
        method: "PUT",
        body: JSON.stringify({ updates }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Save failed");
      const failed = data.results?.filter(r => !r.ok) || [];
      setMsg(failed.length ? `✗ ${failed.length} user(s) failed to save.` : `✓ Permissions saved for ${updates.length} user(s).`);
      load();
    } catch (e) { setMsg(`✗ ${e.message}`); }
    finally { setSaving(false); }
  }

  // Separate editable users from full-access users
  const editableUsers = users.filter(u => u.role !== "super_admin" && u.role !== "admin");
  const fullAccessUsers = users.filter(u => u.role === "super_admin" || u.role === "admin");

  if (loading) return <div style={{ padding: "40px 0", textAlign: "center", color: t.textFaint, fontSize: 13 }}>Loading…</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>Permissions Matrix</h3>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: t.textMuted }}>Toggle section access per user. Super Admin and Admin always have full access.</p>
        </div>
        <button onClick={saveAll} disabled={saving || editableUsers.length === 0}
          style={{ background: saving ? t.surface3 : "#2563EB", border: "none", borderRadius: 10, padding: "9px 20px", color: "#fff", fontSize: 12, fontWeight: 800, cursor: saving ? "default" : "pointer", fontFamily: "Georgia, serif", opacity: editableUsers.length === 0 ? 0.5 : 1 }}>
          {saving ? "Saving…" : "Save All Changes"}
        </button>
      </div>

      {msg && (
        <div style={{ background: msg.startsWith("✓") ? "#22c55e11" : "#DC262611", border: `1px solid ${msg.startsWith("✓") ? "#22c55e33" : "#DC262633"}`, borderRadius: 10, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: msg.startsWith("✓") ? "#22c55e" : "#DC2626", display: "flex", justifyContent: "space-between" }}>
          <span>{msg}</span>
          <button onClick={() => setMsg("")} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 14 }}>×</button>
        </div>
      )}

      {/* Full-access banner */}
      {fullAccessUsers.length > 0 && (
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12, padding: "12px 16px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, marginBottom: 8 }}>Full Access (all sections)</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {fullAccessUsers.map(u => (
              <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 12, color: t.text, fontWeight: 700 }}>{u.name}</span>
                <span style={{ fontSize: 9, color: ROLE_COLORS[u.role] || t.textMuted, background: `${ROLE_COLORS[u.role]}18`, border: `1px solid ${ROLE_COLORS[u.role]}33`, padding: "1px 6px", borderRadius: 10, fontWeight: 700 }}>{ROLE_LABELS[u.role]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Matrix table */}
      {editableUsers.length === 0 ? (
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, padding: "40px 0", textAlign: "center", color: t.textFaint, fontSize: 13 }}>
          No editor or viewer accounts to configure.
        </div>
      ) : (
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, overflow: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 600 }}>
            <thead>
              <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                <th style={{ padding: "12px 16px", textAlign: "left", color: t.textMuted, fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, whiteSpace: "nowrap", minWidth: 160 }}>User</th>
                {SECTIONS.map(s => (
                  <th key={s.key} style={{ padding: "10px 8px", textAlign: "center", color: t.textMuted, fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
                    <div style={{ writingMode: isMobile ? "vertical-rl" : "horizontal-tb", whiteSpace: "nowrap" }}>{s.label}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {editableUsers.map(u => (
                <tr key={u.id} style={{ borderBottom: `1px solid ${t.border}`, transition: "background 0.15s" }}
                  onMouseEnter={e => e.currentTarget.style.background = t.surface2}
                  onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                  <td style={{ padding: "10px 16px", whiteSpace: "nowrap" }}>
                    <div style={{ fontWeight: 700, color: t.text, fontSize: 12 }}>{u.name}</div>
                    <div style={{ fontSize: 10, color: t.textFaint, marginTop: 2 }}>
                      <span style={{ color: ROLE_COLORS[u.role] || t.textMuted, fontWeight: 700 }}>{ROLE_LABELS[u.role] || u.role}</span>
                      {!u.active && <span style={{ marginLeft: 6, color: "#DC2626" }}>· Inactive</span>}
                    </div>
                  </td>
                  {SECTIONS.map(s => {
                    const checked = !!draft[u.id]?.[s.key];
                    return (
                      <td key={s.key} style={{ padding: "10px 8px", textAlign: "center" }}>
                        <button onClick={() => toggle(u.id, s.key)} type="button"
                          style={{
                            width: 22, height: 22, borderRadius: 6, cursor: "pointer", border: "none",
                            background: checked ? "#2563EB" : t.surface3,
                            display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto",
                            transition: "background 0.15s",
                          }}
                          title={checked ? "Revoke access" : "Grant access"}>
                          {checked && <span style={{ color: "#fff", fontSize: 12, lineHeight: 1 }}>✓</span>}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
